/**
 * Custom React Hook for Razorpay Payment Integration
 * Provides easy-to-use payment functionality with loading and error states
 */

import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  PaymentData,
  RazorpayPaymentOptions,
  RazorpaySuccessResponse,
  RazorpayErrorResponse,
  PaymentResult,
} from '../types/razorpay.types';
import { paymentService } from '../services/payment.service';
import { razorpayConfig } from '../razorpay.config';

export const useRazorpay = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Initialize and display Razorpay payment modal
   */
  const initiatePayment = useCallback(
    async (paymentData: PaymentData): Promise<PaymentResult> => {
      setIsLoading(true);
      setError(null);

      try {
        // Step 1: Load Razorpay script
        const scriptLoaded = await paymentService.loadRazorpayScript();
        if (!scriptLoaded) {
          throw new Error('Failed to load Razorpay SDK. Please check your internet connection.');
        }

        // Step 2: Generate a reference ID for tracking (not an order ID)
        const referenceId = `ref_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Step 3: Configure Razorpay options WITHOUT order_id (direct payment)
        const options: RazorpayPaymentOptions = {
          key: razorpayConfig.keyId,
          amount: Math.round(paymentData.totalAmount * 100), // Amount in paise
          currency: razorpayConfig.currency,
          name: razorpayConfig.companyName,
          description: paymentData.description || 'Health Checkup Appointment Payment',
          image: razorpayConfig.companyLogo,
          handler: async (response: RazorpaySuccessResponse) => {
            // Payment successful - handle success
            await handlePaymentSuccess(
              response, 
              paymentData.bookingId, 
              paymentData.bookingReference,
              paymentData.bookingType,
              referenceId,
              paymentData.totalAmount
            );
          },
          prefill: {
            name: paymentData.name,
            email: paymentData.email,
            contact: paymentData.phone,
          },
          notes: {
            bookingId: paymentData.bookingId.toString(),
            bookingReference: paymentData.bookingReference,
            bookingType: paymentData.bookingType,
            referenceId: referenceId,
          },
          theme: razorpayConfig.theme,
          modal: {
            ondismiss: () => {
              setIsLoading(false);
              toast('Payment cancelled', { icon: 'ℹ️' });
            },
            escape: true,
            backdropclose: false,
          },
        } as any; // Using 'as any' to allow payment without order_id

        // Step 4: Open Razorpay checkout
        const razorpay = new window.Razorpay(options);
        
        razorpay.on('payment.failed', (response: RazorpayErrorResponse) => {
          handlePaymentFailure(
            response, 
            paymentData.bookingId,
            paymentData.bookingReference,
            paymentData.bookingType
          );
        });

        razorpay.open();

        // Return a promise that resolves when payment is complete
        return new Promise((resolve) => {
          const checkPaymentStatus = setInterval(() => {
            if (!isLoading) {
              clearInterval(checkPaymentStatus);
              resolve({
                success: !error,
                error: error || undefined,
              });
            }
          }, 500);
        });
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to initiate payment';
        setError(errorMessage);
        setIsLoading(false);
        toast.error(errorMessage);
        
        return {
          success: false,
          error: errorMessage,
        };
      }
    },
    [isLoading, error]
  );

  /**
   * Handle successful payment
   */
  const handlePaymentSuccess = async (
    response: RazorpaySuccessResponse,
    bookingId: number,
    bookingReference: string,
    bookingType: string,
    referenceId: string,
    totalAmount: number
  ) => {
    try {
      // Verify payment (client-side)
      const isVerified = await paymentService.verifyPayment(response, bookingId, bookingReference);

      if (isVerified) {
        // Extract payment method from Razorpay response
        // The method is available in response object (upi, card, netbanking, wallet, etc.)
        const paymentMethod = (response as any).razorpay_payment_method || 
                              (response as any).method || 
                              'online';
        
        // Fetch payment details from Razorpay response
        const paymentDetails = {
          amount: totalAmount,
          currency: 'INR',
          method: paymentMethod,
        };

        // Use payment_id as order_id if order_id is not present
        const orderId = response.razorpay_order_id || referenceId;

        // Update payment status via n8n webhook
        await paymentService.updatePaymentStatus(
          bookingId,
          bookingReference,
          bookingType,
          response.razorpay_payment_id,
          orderId,
          'success',
          paymentDetails
        );

        toast.success('Payment completed successfully!');
        setIsLoading(false);
        
        // Reload page to reflect updated payment status
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        throw new Error('Payment verification failed');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Payment verification failed';
      setError(errorMessage);
      setIsLoading(false);
      toast.error(errorMessage);
    }
  };

  /**
   * Handle failed payment
   */
  const handlePaymentFailure = async (
    response: RazorpayErrorResponse,
    bookingId: number,
    bookingReference: string,
    bookingType: string
  ) => {
    const errorMessage = response.error.description || 'Payment failed';
    setError(errorMessage);
    setIsLoading(false);

    // Update payment status as failed
    await paymentService.updatePaymentStatus(
      bookingId,
      bookingReference,
      bookingType,
      response.error.metadata.payment_id,
      response.error.metadata.order_id,
      'failed'
    );

    toast.error(errorMessage);
  };

  return {
    initiatePayment,
    isLoading,
    error,
  };
};
